import React, { useState } from 'react';
import { changePassword } from '../../../api/user/account';
import './AccountManagement.css';

const LoginSecurity = () => {
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const togglePasswordVisibility = (field) => {
    setShowPassword({
      ...showPassword,
      [field]: !showPassword[field]
    });
  };

  const validateForm = () => {
    if (!formData.currentPassword || !formData.newPassword || !formData.confirmPassword) {
      setMessage({ type: 'error', text: 'All fields are required' });
      return false;
    }

    if (formData.newPassword.length < 6) {
      setMessage({ type: 'error', text: 'New password must be at least 6 characters long' });
      return false;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      return false;
    }

    if (formData.currentPassword === formData.newPassword) {
      setMessage({ type: 'error', text: 'New password must be different from current password' });
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    if (!validateForm()) return;

    setIsLoading(true);

    try {
      const response = await changePassword({
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword
      });

      if (response.success) {
        setMessage({ type: 'success', text: response.message });
        setFormData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setIsLoading(false);
    }
  };

  const getPasswordStrength = (password) => {
    if (!password) return { strength: 0, label: '' };
    
    let strength = 0;
    if (password.length >= 6) strength += 1;
    if (password.match(/[a-z]/) && password.match(/[A-Z]/)) strength += 1;
    if (password.match(/\d/)) strength += 1;
    if (password.match(/[^a-zA-Z\d]/)) strength += 1;

    const labels = ['Very Weak', 'Weak', 'Fair', 'Strong', 'Very Strong'];
    return { strength, label: labels[strength] };
  };

  const passwordStrength = getPasswordStrength(formData.newPassword);

  return (
    <div className="account-management-section">
      <div className="account-section-header">
        <h2 className="account-section-title">
          <i className="fa fa-shield-alt"></i>
          Login & Security
        </h2>
        <p className="account-section-subtitle">Manage your password and account security</p>
      </div>

      {message.text && (
        <div className={`message ${message.type}`}>
          <i className={`fa ${message.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'}`}></i>
          {message.text}
        </div>
      )}

      <div className="account-content">
        <form className="account-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label" htmlFor="currentPassword">
              Current Password *
            </label>
            <div className="password-input-container">
              <input
                className="form-input"
                type={showPassword.current ? 'text' : 'password'}
                id="currentPassword"
                name="currentPassword"
                value={formData.currentPassword}
                onChange={handleChange}
                placeholder="Enter your current password"
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => togglePasswordVisibility('current')}
              >
                <i className={`fa ${showPassword.current ? 'fa-eye-slash' : 'fa-eye'}`}></i>
              </button>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="newPassword">
              New Password *
            </label>
            <div className="password-input-container">
              <input
                className="form-input"
                type={showPassword.new ? 'text' : 'password'}
                id="newPassword"
                name="newPassword"
                value={formData.newPassword}
                onChange={handleChange}
                placeholder="Enter your new password"
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => togglePasswordVisibility('new')}
              >
                <i className={`fa ${showPassword.new ? 'fa-eye-slash' : 'fa-eye'}`}></i>
              </button>
            </div>
            
            {formData.newPassword && (
              <div className="password-strength">
                <div className="strength-bar">
                  <div 
                    className={`strength-fill strength-${passwordStrength.strength}`}
                    style={{ width: `${(passwordStrength.strength / 4) * 100}%` }}
                  ></div>
                </div>
                <span className={`strength-label strength-${passwordStrength.strength}`}>
                  {passwordStrength.label}
                </span>
              </div>
            )}

            <div className="password-requirements">
              <p className="requirements-title">Password must contain:</p>
              <ul className="requirements-list">
                <li className={formData.newPassword.length >= 6 ? 'met' : ''}>
                  <i className={`fa ${formData.newPassword.length >= 6 ? 'fa-check' : 'fa-times'}`}></i>
                  At least 6 characters
                </li>
                <li className={formData.newPassword.match(/[a-z]/) && formData.newPassword.match(/[A-Z]/) ? 'met' : ''}>
                  <i className={`fa ${formData.newPassword.match(/[a-z]/) && formData.newPassword.match(/[A-Z]/) ? 'fa-check' : 'fa-times'}`}></i>
                  Upper & lowercase letters
                </li>
                <li className={formData.newPassword.match(/\d/) ? 'met' : ''}>
                  <i className={`fa ${formData.newPassword.match(/\d/) ? 'fa-check' : 'fa-times'}`}></i>
                  At least one number
                </li>
                <li className={formData.newPassword.match(/[^a-zA-Z\d]/) ? 'met' : ''}>
                  <i className={`fa ${formData.newPassword.match(/[^a-zA-Z\d]/) ? 'fa-check' : 'fa-times'}`}></i>
                  Special character
                </li>
              </ul>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="confirmPassword">
              Confirm New Password *
            </label>
            <div className="password-input-container">
              <input
                className="form-input"
                type={showPassword.confirm ? 'text' : 'password'}
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Re-enter your new password"
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => togglePasswordVisibility('confirm')}
              >
                <i className={`fa ${showPassword.confirm ? 'fa-eye-slash' : 'fa-eye'}`}></i>
              </button>
            </div>
            
            {formData.confirmPassword && (
              <div className={`password-match ${formData.newPassword === formData.confirmPassword ? 'match' : 'no-match'}`}>
                <i className={`fa ${formData.newPassword === formData.confirmPassword ? 'fa-check' : 'fa-times'}`}></i>
                {formData.newPassword === formData.confirmPassword ? 'Passwords match' : 'Passwords do not match'}
              </div>
            )}
          </div>

          <div className="form-actions">
            <button
              type="submit"
              className="save-btn primary"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <div className="btn-spinner"></div>
                  Changing Password...
                </>
              ) : (
                <>
                  <i className="fa fa-key"></i>
                  Change Password
                </>
              )}
            </button>
          </div>
        </form>

        <div className="security-tips">
          <h3 className="tips-title">
            <i className="fa fa-lightbulb"></i>
            Security Tips
          </h3>
          <ul className="tips-list">
            <li>
              <i className="fa fa-check"></i>
              Use a unique password that you don't use elsewhere
            </li>
            <li>
              <i className="fa fa-check"></i>
              Change your password regularly
            </li>
            <li>
              <i className="fa fa-check"></i>
              Never share your password with anyone
            </li>
            <li>
              <i className="fa fa-check"></i>
              Use a password manager to store your passwords securely
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default LoginSecurity;